import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, User, Trophy, Target, Award, TrendingUp, Activity, Zap, ChevronRight, Calendar, MapPin, Mail, Phone } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import axios from 'axios';
import { API } from '@/App';

export default function PlayerProfilePage({ user, setUser }) {
  const { playerId } = useParams();
  const [playerData, setPlayerData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPlayerStats();
  }, [playerId]);

  const fetchPlayerStats = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/players/${playerId}/detailed-stats`);
      setPlayerData(response.data);
    } catch (error) {
      console.error('Error fetching player stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading player profile...</p>
        </div>
      </div>
    );
  }

  if (!playerData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <h2 className="text-xl font-bold text-gray-800">Player not found</h2>
          <Link to="/stats" className="mt-4 text-blue-600 hover:underline">Back to Stats</Link>
        </div>
      </div>
    );
  }

  const { player, team, league, career_batting, career_bowling, recent_form } = playerData;

  const StatBox = ({ label, value, subValue, icon: Icon, color }) => (
    <div className={`${color} rounded-xl p-4 text-white`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-white/80 text-sm">{label}</span>
        {Icon && <Icon className="w-5 h-5 text-white/60" />}
      </div>
      <div className="text-3xl font-black">{value}</div>
      {subValue && <div className="text-white/70 text-xs mt-1">{subValue}</div>}
    </div>
  );

  const getRoleColor = (role) => {
    switch (role?.toLowerCase()) {
      case 'batsman': return 'bg-[#8B1E3F]';
      case 'bowler': return 'bg-purple-500';
      case 'all-rounder': return 'bg-green-500';
      case 'wicketkeeper': return 'bg-blue-500';
      default: return 'bg-gray-500';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-100 to-white pb-24">
      {/* Header */}
      <header className="bg-gradient-to-r from-blue-700 via-indigo-700 to-purple-800 text-white">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <Link to="/stats" className="text-white/80 hover:text-white flex items-center gap-2 mb-6">
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Stats</span>
          </Link>
          
          <div className="flex items-start gap-6">
            {/* Player Avatar */}
            <div className="w-24 h-24 md:w-32 md:h-32 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
              <User className="w-12 h-12 md:w-16 md:h-16 text-white/70" />
            </div>
            
            {/* Player Info */}
            <div className="flex-1">
              <h1 className="text-2xl md:text-4xl font-black mb-2" data-testid="player-name">
                {player?.name}
              </h1>
              <div className="flex flex-wrap items-center gap-3 mb-3">
                <Badge className={`${getRoleColor(player?.player_role)} text-white`}>
                  {player?.player_role || 'Player'}
                </Badge>
                <span className="text-white/80">{team?.name}</span>
              </div>
              <div className="flex flex-wrap gap-4 text-sm text-white/70">
                <div className="flex items-center gap-1">
                  <Trophy className="w-4 h-4" />
                  <span>{league?.name || 'League'}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Activity className="w-4 h-4" />
                  <span>{player?.batting_style} / {player?.bowling_style}</span>
                </div>
              </div>
              {/* Contact Info */}
              <div className="flex flex-wrap gap-4 mt-3 text-sm">
                {player?.email && (
                  <a href={`mailto:${player.email}`} className="flex items-center gap-1 text-white/80 hover:text-white">
                    <Mail className="w-4 h-4" />
                    <span>{player.email}</span>
                  </a>
                )}
                {player?.phone && (
                  <a href={`tel:${player.phone}`} className="flex items-center gap-1 text-white/80 hover:text-white">
                    <Phone className="w-4 h-4" />
                    <span>{player.phone}</span>
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-8 space-y-8">
        {/* Key Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatBox 
            label="Total Runs" 
            value={career_batting?.runs || 0}
            subValue={`${career_batting?.innings || 0} innings`}
            icon={Trophy}
            color="bg-gradient-to-br from-[#8B1E3F] to-[#6D1830]"
          />
          <StatBox 
            label="Total Wickets" 
            value={career_bowling?.wickets || 0}
            subValue={`${career_bowling?.overs || 0} overs`}
            icon={Target}
            color="bg-gradient-to-br from-purple-500 to-violet-600"
          />
          <StatBox 
            label="Strike Rate" 
            value={career_batting?.strike_rate || 0}
            subValue="Batting"
            icon={Zap}
            color="bg-gradient-to-br from-red-500 to-rose-500"
          />
          <StatBox 
            label="Economy" 
            value={career_bowling?.economy || 0}
            subValue="Bowling"
            icon={TrendingUp}
            color="bg-gradient-to-br from-teal-500 to-cyan-500"
          />
        </div>

        {/* Batting Stats Card */}
        <Card className="overflow-hidden border-0 shadow-lg">
          <CardHeader className="bg-gradient-to-r from-[#8B1E3F] to-[#6D1830] text-white">
            <CardTitle className="flex items-center gap-3">
              <Trophy className="w-6 h-6" />
              Batting Statistics
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="text-3xl font-black text-gray-800">{career_batting?.matches || 0}</div>
                <div className="text-sm text-gray-500">Matches</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-black text-gray-800">{career_batting?.innings || 0}</div>
                <div className="text-sm text-gray-500">Innings</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-black text-[#8B1E3F]">{career_batting?.runs || 0}</div>
                <div className="text-sm text-gray-500">Runs</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-black text-gray-800">{career_batting?.average || 0}</div>
                <div className="text-sm text-gray-500">Average</div>
              </div>
            </div>
            
            <div className="border-t mt-6 pt-6">
              <div className="grid grid-cols-3 md:grid-cols-6 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold text-blue-600">{career_batting?.highest || 0}</div>
                  <div className="text-xs text-gray-500">Highest</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-red-500">{career_batting?.strike_rate || 0}</div>
                  <div className="text-xs text-gray-500">SR</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-green-600">{career_batting?.hundreds || 0}</div>
                  <div className="text-xs text-gray-500">100s</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-amber-500">{career_batting?.fifties || 0}</div>
                  <div className="text-xs text-gray-500">50s</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-blue-500">{career_batting?.fours || 0}</div>
                  <div className="text-xs text-gray-500">4s</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-green-500">{career_batting?.sixes || 0}</div>
                  <div className="text-xs text-gray-500">6s</div>
                </div>
              </div>
            </div>
            
            <div className="border-t mt-6 pt-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Balls Faced</span>
                  <span className="font-semibold">{career_batting?.balls || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Not Outs</span>
                  <span className="font-semibold">{career_batting?.not_outs || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Ducks</span>
                  <span className="font-semibold">{career_batting?.ducks || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Boundary %</span>
                  <span className="font-semibold">{career_batting?.boundary_percent || 0}%</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Bowling Stats Card */}
        <Card className="overflow-hidden border-0 shadow-lg">
          <CardHeader className="bg-gradient-to-r from-purple-500 to-violet-600 text-white">
            <CardTitle className="flex items-center gap-3">
              <Target className="w-6 h-6" />
              Bowling Statistics
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="text-3xl font-black text-gray-800">{career_bowling?.matches || 0}</div>
                <div className="text-sm text-gray-500">Matches</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-black text-gray-800">{career_bowling?.innings || 0}</div>
                <div className="text-sm text-gray-500">Innings</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-black text-purple-600">{career_bowling?.wickets || 0}</div>
                <div className="text-sm text-gray-500">Wickets</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-black text-gray-800">{career_bowling?.average || 0}</div>
                <div className="text-sm text-gray-500">Average</div>
              </div>
            </div>
            
            <div className="border-t mt-6 pt-6">
              <div className="grid grid-cols-3 md:grid-cols-6 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold text-teal-500">{career_bowling?.overs || 0}</div>
                  <div className="text-xs text-gray-500">Overs</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-red-500">{career_bowling?.runs || 0}</div>
                  <div className="text-xs text-gray-500">Runs</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-blue-500">{career_bowling?.economy || 0}</div>
                  <div className="text-xs text-gray-500">Economy</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-green-600">{career_bowling?.best || '0/0'}</div>
                  <div className="text-xs text-gray-500">Best</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-amber-500">{career_bowling?.strike_rate || 0}</div>
                  <div className="text-xs text-gray-500">SR</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-purple-500">{career_bowling?.five_wickets || 0}</div>
                  <div className="text-xs text-gray-500">5W</div>
                </div>
              </div>
            </div>
            
            <div className="border-t mt-6 pt-6">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Wides</span>
                  <span className="font-semibold">{career_bowling?.wides || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">No Balls</span>
                  <span className="font-semibold">{career_bowling?.no_balls || 0}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Recent Form */}
        {recent_form && recent_form.length > 0 && (
          <Card className="overflow-hidden border-0 shadow-lg">
            <CardHeader className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white">
              <CardTitle className="flex items-center gap-3">
                <Activity className="w-6 h-6" />
                Recent Form (Last 5 Matches)
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y">
                {recent_form.map((match, idx) => (
                  <div key={match.match_id} className="p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-gray-800">vs {match.opponent}</span>
                          {match.date && (
                            <span className="text-xs text-gray-400">
                              {new Date(match.date).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                        <div className="flex gap-4 text-sm">
                          {match.batting && (
                            <span className={`${match.batting.runs >= 50 ? 'text-[#8B1E3F] font-bold' : 'text-gray-600'}`}>
                              {match.batting.runs} ({match.batting.balls})
                              {match.batting.out ? '' : '*'}
                            </span>
                          )}
                          {match.bowling && match.bowling.overs > 0 && (
                            <span className={`${match.bowling.wickets >= 3 ? 'text-purple-500 font-bold' : 'text-gray-600'}`}>
                              {match.bowling.wickets}/{match.bowling.runs} ({match.bowling.overs} ov)
                            </span>
                          )}
                        </div>
                      </div>
                      <Link to={`/matches/${match.match_id}`}>
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
